import { Button } from '@heidi/design-system/button';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { isMac, isTauri } from '@heidi/runtime/platform';
import { cn } from '@heidi/utils';
import { MicOff, RotateCcw } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import { useVoiceAssistantStore } from '../store/useVoiceAssistantStore';
import { voiceAssistantMessages as messages } from './voice-assistant.messages';

interface VoiceMicPermissionDeniedProps {
  className?: string;
}

/**
 * Recovery state shown when Talk to Heidi cannot capture the microphone
 * because permission was denied. Self-gates on the store flag so it can be
 * dropped into any voice home (dock, overlay window, docked window).
 *
 * Copy is platform-aware: the desktop app captures natively, so browser
 * guidance ("allow access in your browser") is meaningless there — desktop
 * points at the OS settings instead (see the `isTauri()` branching in
 * `dictation-permissions.tsx` and the `no-unguarded-browser-media` rule).
 * No browser permission API is queried here.
 */
export function VoiceMicPermissionDenied({ className }: VoiceMicPermissionDeniedProps) {
  const denied = useVoiceAssistantStore((s) => s.micPermissionDenied);
  const { open } = useVoiceAssistant();

  if (!denied) return null;

  const body = isTauri()
    ? isMac
      ? messages.micDeniedBodyMac
      : messages.micDeniedBodyWindows
    : messages.micDeniedBodyWeb;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col gap-2 rounded-xl border border-validation-warning-border bg-validation-warning-muted/40 px-3 py-2.5',
        className
      )}
      data-testid="voice-mic-permission-denied"
    >
      <div className="flex items-center gap-2">
        <MicOff className="size-4 shrink-0 text-validation-warning" aria-hidden />
        <TypographyP2Bold className="text-balance text-text-primary">
          <FormattedMessage {...messages.micDeniedTitle} />
        </TypographyP2Bold>
      </div>
      <TypographyP2 className="text-pretty text-text-secondary">
        <FormattedMessage {...body} />
      </TypographyP2>
      <Button variant="outline" size="sm" className="self-start" onClick={() => open()}>
        <RotateCcw className="size-4" />
        <FormattedMessage {...messages.micDeniedRetry} />
      </Button>
    </div>
  );
}
